"use client";

import Image, { StaticImageData } from "next/image";
import React, { useRef, useState } from "react";
import { Images } from "@/app/assets/shared";

interface Logo {
  src: string | StaticImageData;
  name: string;
}

interface BannerProps {
  title?: React.ReactNode;
  desc?: string;
  logoSrc: string | StaticImageData;
  logoAlt?: string;
  glowColor?: string;

  // Optional — existing components remain unchanged
  showMarquee?: boolean;
}

const TechnologyBanner = ({
  title = "",
  desc = "",
  logoSrc,
  logoAlt = "Technology Logo",
  glowColor = "rgba(0, 98, 255, 0.9)",
  showMarquee = false,
}: BannerProps) => {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    const { left, top, width, height } =
      containerRef.current.getBoundingClientRect();

    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setMousePos({ x, y });
  };

  const logos: Logo[] = [
    {
      src: Images?.logoSet.delMonte,
      name: "DelMonte",
    },
    {
      src: Images?.logoSet.festivity,
      name: "Festivity",
    },
    {
      src: Images?.logoSet.nasscom,
      name: "NASSCOM",
    },
    {
      src: Images?.logoSet.paceFunding,
      name: "PaceFunding",
    },
    {
      src: Images?.logoSet.rayTech,
      name: "RayTech",
    },
    {
      src: Images?.logoSet.unitedNations,
      name: "United Nations",
    },
    {
      src: Images?.logoSet.volkswagen,
      name: "Volkswagen",
    },
  ];

  return (
    <div className="w-[90%] max-w-[2500px] mx-auto relative min-h-[40vh] lg:min-h-[50vh] flex flex-col justify-center py-10">
      {/* Hero Content */}
      <div className="grid grid-cols-1 lg:grid-cols-10 w-full items-center gap-6 lg:gap-10">
        {/* Left Side: Content */}
        <div className="lg:col-span-7 flex flex-col">
          <h1 className="text-black text-4xl sm:text-5xl md:text-6xl lg:text-[length:var(--service-banner-title)] clash-font capitalize leading-[1.2] lg:leading-[1.1]">
            <span>{title}</span>
          </h1>

          <p className="text-[#636364] text-base md:text-lg lg:text-[length:var(--banner-para)] max-w-[65ch] mt-4 lg:mt-8">
            {desc}
          </p>
        </div>

        {/* Right Side: Interactive Reveal Logo */}
        <div className="hidden lg:col-span-3 lg:flex justify-end items-center relative">
          <div
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="relative w-full max-w-[300px] xl:max-w-[480px] cursor-pointer"
          >
            {/* Base Logo */}
            <Image
              src={logoSrc}
              alt={logoAlt}
              width={600}
              height={600}
              className="w-full h-auto object-contain select-none pointer-events-none opacity-50 bg-[#f4f8fc]"
              sizes="(min-width: 1280px) 480px, 300px"
              priority
              fetchPriority="high"
            />

            {/* Hover Highlight */}
            <div
              className={`absolute inset-0 transition-opacity duration-300 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              style={{
                maskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
                WebkitMaskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
                filter: `hue-rotate(200deg) saturate(150%) drop-shadow(0 0 20px ${glowColor})`,
              }}
            >
              <Image
                src={logoSrc}
                alt={`${logoAlt} Highlight`}
                width={600}
                height={600}
                className="w-full h-auto object-contain select-none pointer-events-none"
                sizes="(min-width: 1280px) 480px, 300px"
              />
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          Trusted By Marquee
          Only appears when showMarquee={true}
         ===================================================== */}
      {showMarquee && logos.length > 0 && (
        <div className="mt-10 sm:mt-12 lg:mt-[70px] w-full">
          {/* Trusted By Heading */}
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-7 sm:mb-9 lg:mb-12">
            <span className="h-px w-10 sm:w-14 lg:w-20 bg-[linear-gradient(90deg,transparent_0%,#626262_100%)]" />

            <p className="text-[#636364] text-[13px] sm:text-[17px] lg:text-[24px] font-normal uppercase tracking-[0.15em] sm:tracking-[0.2em] lg:tracking-widest whitespace-nowrap">
              Trusted By
            </p>

            <span className="h-px w-10 sm:w-14 lg:w-20 bg-[linear-gradient(90deg,#626262_0%,transparent_100%)]" />
          </div>

          {/* Logos */}
          <div
            className="relative overflow-hidden w-full"
            style={{
              maskImage:
                "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
            }}
          >
            <div className="flex w-max animate-marquee gap-8 sm:gap-12 lg:gap-[70px] items-center">
              {[...logos, ...logos].map((logo, i) => (
                <div
                  key={i}
                  className="
                    flex-shrink-0
                    flex items-center justify-center
                    h-[70px]
                    sm:h-[90px]
                    lg:h-[120px]
                    opacity-70
                    hover:opacity-100
                    transition-opacity duration-300
                  "
                >
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={160}
                    height={120}
                    className="
                      h-full
                      w-auto
                      max-w-[90px]
                      sm:max-w-[115px]
                      lg:max-w-[140px]
                      object-contain
                    "
                    sizes="(max-width: 640px) 90px, (max-width: 1024px) 115px, 140px"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnologyBanner;
